import { useState } from '@hookstate/core';
import { useContext, useState as classicUseState } from 'react';
import ReactTooltip from 'react-tooltip';

import { Icon as BlueIcon } from '@blueprintjs/core';

import SerachIcon from '@assets/svg/table-search.svg';
import UpIcon from '@assets/svg/table-up.svg';
import DownIcon from '@assets/svg/table-down.svg';
import CheckboxIcon from '@assets/svg/checkbox-checked.svg';
import CheckAllIcon from '@assets/svg/check-all.svg';
import CloseImage from '@assets/svg/small-cross.svg';

import { getTagsCategory } from '@services/queries/tagsCategory/getTagsCategory/getTagsCategory';
import { useQuery } from 'react-query';
import { format } from 'date-fns';

import { NavigationContext } from '@layout/navigation/navigation';
import { InviteUserRole } from '@models/Invite.model';
import { ITag } from '@models/Tag.model';
import { AddNewMenu } from './Components/AddNewMenu/AddNewMenu';
import { CategoriesMenu } from './Components/CategoriesMenu/CategoriesMenu';
import { CombineDialog } from './Components/CombineDialog/CombineDialog';
import { MoveDialog } from './Components/TagsMenu/Components/MoveDialog/MoveDialog';
import { DeleteDialog } from './Components/TagsMenu/Components/DeleteDialog/DeleteDialog';

import {
  Container,
  Title,
  Line,
  InputContainer,
  InputSearch,
  Row,
  Button,
  Divider,
  SearchIconContainer,
  IconContainer,
  ColumnContainer,
  ColumnName,
  Direction,
  Spacing,
  Header,
  LineDivider,
  Item,
  RowDiv,
  ColumnDiv,
  Color,
  Text,
  ChevronDiv,
  ActionButtons,
  Checkbox,
  MenuDiv,
  Table,
  HeaderWrapper,
  Close,
} from './TagsManagement.styles';
import { TagsLoading } from './Components/loading/Tags.loading';
import { TagsList } from '../TagsList/TagsList';

interface IColumnTitle {
  label: string;
  direction?: 'up' | 'down';
  onClick?: () => void;
}

export type SortTagsTypes =
  | 'tag_category.name'
  | 'tag_category.description'
  | 'tag_category.lastUsed'
  | undefined;

const ColumnTitle = ({ label, direction, ...rest }: IColumnTitle) => {
  return (
    <ColumnContainer {...rest}>
      <ColumnName>{label}</ColumnName>
      {!!direction && (
        <Direction>
          <img src={direction === 'up' ? UpIcon : DownIcon} alt="direction" />
        </Direction>
      )}
    </ColumnContainer>
  );
};

const TagsManagement = () => {
  const [open, setOpen] = classicUseState<string[]>([]);

  const toggleOpen = (id: string) => {
    if (open.find(item => item === id)) {
      setOpen(open.filter(item => item !== id));
    } else {
      setOpen([...open, id]);
    }
  };

  const [checked, setChecked] = classicUseState<string[]>([]);
  const [tagId, setTagId] = classicUseState<string[]>([]);
  const [selectedTags, setSelectedTags] = classicUseState<ITag[]>([]);

  const { role } = useContext(NavigationContext);

  const toggleCheckBox = (id: string) => {
    if (checked.find(item => item === id)) {
      setChecked(checked.filter(item => item !== id));
    } else {
      setChecked([...checked, id]);
    }
  };

  const toggleSelectedTags = (Tag: ITag) => {
    if (selectedTags.find(item => item === Tag)) {
      setSelectedTags(selectedTags.filter(item => item !== Tag));
    } else {
      setSelectedTags([...selectedTags, Tag]);
    }
  };

  const toggleCheckBoxArray = (ids: string[]) => {
    if (ids.some(id => !checked.find(item => item === id))) {
      setChecked([...checked, ...ids]);
    } else {
      setChecked(checked.filter(item => !ids.find(id => item === id)));
    }
  };

  const toggleSelectedTagsArray = (tag: ITag[]) => {
    if (tag.some(id => !selectedTags.find(item => item === id))) {
      setSelectedTags([...selectedTags, ...tag]);
    } else {
      setSelectedTags(
        selectedTags.filter(item => !tag.find(id => item === id)),
      );
    }
  };

  const [searchTextModel, setSearchTextModel] = classicUseState('');
  const [searchText, setSearchText] = classicUseState('');
  const [direction, setDirection] = classicUseState<'ASC' | 'DESC' | undefined>(
    undefined,
  );
  const [sort, setSort] = classicUseState<string | undefined>(undefined);

  const { data, isLoading } = useQuery(
    ['tagCategories', searchText, sort, direction],
    () =>
      getTagsCategory({
        search: searchText,
        sort,
        direction,
      }),
  );

  const [isManage, setIsManage] = classicUseState<boolean>(true);

  const openDeleteDialog = useState(false);
  const [openMoveDialog, setOpenMoveDialog] = classicUseState<boolean>(false);

  const openDeleteTagDialog = useState(false);
  const [openMoveTagDialog, setOpenMoveTagDialog] =
    classicUseState<boolean>(false);

  const [openCombineDialog, setOpenCombineDialog] =
    classicUseState<boolean>(false);

  const isSelected = checked.length > 0;

  const ManageButtons = () => {
    if (!isManage && !isSelected)
      return (
        <Button
          onClick={() => {
            setIsManage(true);
            setChecked([]);
            setSelectedTags([]);
          }}
        >
          Cancel
        </Button>
      );
    if (!isManage && isSelected)
      return (
        <Row className="manage">
          <Button
            onClick={() => {
              setIsManage(true);
              setChecked([]);
              setSelectedTags([]);
            }}
          >
            Cancel
          </Button>
          <ActionButtons
            background="#1371fe"
            onClick={() => setOpenCombineDialog(true)}
          >
            Combine
          </ActionButtons>
          <ActionButtons
            background="#1371fe"
            onClick={() => setOpenMoveDialog(true)}
          >
            Move
          </ActionButtons>
          <ActionButtons
            background="#D14343"
            onClick={() => openDeleteDialog.set(true)}
          >
            Delete
          </ActionButtons>
        </Row>
      );
    return (
      <Button
        onClick={() => setIsManage(false)}
        disabled={![InviteUserRole.ADMIN, InviteUserRole.EDITOR].includes(role)}
      >
        <IconContainer>
          <BlueIcon icon="manually-entered-data" size={16} color="#8F95B2" />
        </IconContainer>
        Manage
      </Button>
    );
  };

  const clickColumn = (column: SortTagsTypes) => {
    if (sort === column) {
      if (direction === 'ASC') {
        setDirection('DESC');
        return;
      }
      if (direction === 'DESC') {
        setSort(undefined);
        return;
      }
      setDirection('ASC');
    } else {
      setDirection('ASC');
    }
    setSort(column);
  };

  const directionResult = (
    column: SortTagsTypes,
  ): 'up' | 'down' | undefined => {
    if (sort === column) {
      if (direction === 'ASC') {
        return 'up';
      }
      if (direction === 'DESC') {
        return 'down';
      }
    }
    return undefined;
  };

  const HeaderConditions = () => {
    const tagsIdsNested = data?.map(item =>
      item.tags ? item.tags.map(tag => tag.id) : [],
    );
    const tagsIds = tagsIdsNested ? tagsIdsNested.flat(1) : [];
    const isAllChecked = !tagsIds.some(
      id => !checked.find(item => item === id),
    );
    const tagsNested = data?.map(item =>
      item.tags ? item.tags.map(tag => tag) : [],
    );
    const tags = tagsNested ? tagsNested.flat(1) : [];
    if (!isManage)
      return (
        <Row>
          <Checkbox
            onClick={() => {
              toggleCheckBoxArray(tagsIds);
              toggleSelectedTagsArray([]);
              toggleSelectedTagsArray(tags);
            }}
            checked={isAllChecked}
          >
            {isAllChecked ? (
              <img src={CheckboxIcon} alt="" width="14px" height="14px" />
            ) : (
              ''
            )}
          </Checkbox>
          <ColumnName>
            {checked.length > 0
              ? `${checked.length} tags selected`
              : 'Select a tag or group below'}
          </ColumnName>
        </Row>
      );
    return (
      <Header>
        <ColumnTitle
          label="Name"
          onClick={() => clickColumn('tag_category.name')}
          direction={directionResult('tag_category.name')}
        />
        <ColumnTitle
          label="Description"
          onClick={() => clickColumn('tag_category.description')}
          direction={directionResult('tag_category.description')}
        />
        <ColumnTitle
          label="Last used"
          onClick={() => clickColumn('tag_category.lastUsed')}
          direction={directionResult('tag_category.lastUsed')}
        />
        <ColumnTitle
          label="# of items"
          // onClick={() => clickColumn()}
          // direction={directionResult()}
        />
      </Header>
    );
  };

  return (
    <Container>
      <Line>
        <Title>Tag Management</Title>
        <Row>
          <InputContainer>
            <SearchIconContainer>
              <img src={SerachIcon} alt="search" />
            </SearchIconContainer>
            <InputSearch
              placeholder="Find a tag"
              value={searchTextModel}
              onChange={e => setSearchTextModel(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  setSearchText(searchTextModel);
                }
              }}
              autoFocus
            />
            {searchTextModel !== '' && !isLoading && (
              <Close
                src={CloseImage}
                alt="close"
                onClick={() => {
                  if (setSearchTextModel) setSearchTextModel('');
                  if (setSearchText) setSearchText('');
                }}
              />
            )}
          </InputContainer>

          <Button className="addButton">
            <AddNewMenu />
          </Button>

          {ManageButtons()}
        </Row>
      </Line>
      <Divider height="24px" />

      <Table>
        <HeaderWrapper>
          <Spacing>{HeaderConditions()}</Spacing>
          <LineDivider />
        </HeaderWrapper>

        {data && !isLoading ? (
          data?.map(tagCategory => {
            const isOpen = !!open.find(item => item === tagCategory.id);
            const tagsIds = tagCategory.tags
              ? tagCategory.tags.map(item => item.id)
              : [];
            const isCategoryChecked =
              tagsIds.length > 0 &&
              !tagsIds.some(id => !checked.find(item => item === id));
            const tags = tagCategory.tags
              ? tagCategory.tags.map(item => item)
              : [];
            return (
              <Spacing key={tagCategory.id}>
                <Row>
                  <Item
                    grid={!isManage}
                    onClick={() => toggleOpen(tagCategory.id)}
                  >
                    {!isManage ? (
                      <Checkbox
                        data-tip
                        data-for={`helpTooltip ${tagCategory.id}`}
                        onClick={e => {
                          e.stopPropagation();
                          toggleCheckBoxArray(tagsIds);
                          toggleSelectedTagsArray(tags);
                        }}
                        checked={isCategoryChecked}
                      >
                        {isCategoryChecked ? (
                          <img
                            src={CheckAllIcon}
                            alt=""
                            width="14px"
                            height="14px"
                            data-tip
                            data-for="helpTooltip"
                          />
                        ) : (
                          ''
                        )}

                        <ReactTooltip
                          className="tooltip"
                          data-for={`helpTooltip ${tagCategory.id}`}
                          effect="solid"
                          place="top"
                          multiline
                        >
                          Management actions are tag-specific. <br /> Therefore,
                          selecting a category selects all <br /> tags under it.
                          If you want to manage the <br /> category itself,
                          click the actions “...” menu.
                        </ReactTooltip>
                      </Checkbox>
                    ) : (
                      ''
                    )}
                    <RowDiv>
                      <ChevronDiv>
                        {isOpen ? (
                          <IconContainer>
                            <BlueIcon
                              icon="chevron-up"
                              size={16}
                              color="#8F95B2"
                            />
                          </IconContainer>
                        ) : (
                          <IconContainer>
                            <BlueIcon
                              icon="chevron-down"
                              size={16}
                              color="#8F95B2"
                            />
                          </IconContainer>
                        )}
                      </ChevronDiv>

                      <Color color={tagCategory.color} />

                      <ColumnDiv>
                        <Text>{tagCategory.name}</Text>
                        <Divider height="6px" />
                        <Text className="subText">{`${tagCategory.tagsCount} tags`}</Text>
                      </ColumnDiv>
                    </RowDiv>

                    <Text light limitOfLines={1}>
                      {tagCategory.description}
                    </Text>

                    <Text light>
                      {tagCategory?.lastUsed &&
                        format(tagCategory.lastUsed, 'MM/dd/yyyy')}
                    </Text>

                    <Text light className="noDescription">
                      -
                    </Text>

                    <MenuDiv
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      <CategoriesMenu tagCategory={tagCategory} />
                    </MenuDiv>
                  </Item>
                </Row>
                <LineDivider />
                {isOpen && (
                  <>
                    <TagsList
                      sort={sort}
                      searchText={searchText}
                      tagCategoryId={tagCategory.id}
                      isManage={isManage}
                      tagCategory={tagCategory}
                      toggleCheckBox={toggleCheckBox}
                      checked={checked}
                      setOpenMoveTagDialog={setOpenMoveTagDialog}
                      setTagId={setTagId}
                      openDeleteTagDialog={openDeleteTagDialog}
                      toggleSelectedTags={toggleSelectedTags}
                    />
                  </>
                )}
              </Spacing>
            );
          })
        ) : (
          <TagsLoading />
        )}
      </Table>

      {openDeleteDialog.value && (
        <DeleteDialog
          open={openDeleteDialog}
          tagsIds={checked}
          onDelete={() => setChecked([])}
          selectedTags={selectedTags}
        />
      )}

      {openMoveDialog && (
        <MoveDialog
          tagsIds={checked}
          open={openMoveDialog}
          onClose={() => {
            setOpenMoveDialog(false);
          }}
          setChecked={setChecked}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
        />
      )}

      {openDeleteTagDialog.value && (
        <DeleteDialog
          open={openDeleteTagDialog}
          tagsIds={tagId}
          onDelete={() => toggleCheckBoxArray(tagId)}
          selectedTags={selectedTags}
        />
      )}

      {openMoveTagDialog && (
        <MoveDialog
          tagsIds={tagId}
          open={openMoveTagDialog}
          onClose={() => {
            setOpenMoveTagDialog(false);
          }}
          setChecked={setChecked}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
        />
      )}

      {openCombineDialog && (
        <CombineDialog
          tagsIds={checked}
          open={openCombineDialog}
          onClose={() => {
            setOpenCombineDialog(false);
          }}
          setChecked={setChecked}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
        />
      )}
    </Container>
  );
};

export { TagsManagement };
